import React, { useEffect, useMemo } from "react";
import { Input, Button, Card, Row, Col, Switch } from "antd";
import { CalculatorOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Typography } from "@mui/material";
import { Button as MUIButton } from "@mui/material";
import BadgeButton from "components/AdminBooks/BadgeButton";

export const PROFICIENCY_LEVEL_LABELS = [
  "Novice",
  "Early Intermediate",
  "Intermediate",
  "Advance",
  "Advance High",
];

export const splitProficiencyPointsAcrossLevels = (maxPoint, levelCount, existingLevels = []) => {
  const count = Math.max(1, levelCount);
  const maxVal = Math.max(0, Math.floor(Number(maxPoint) || 0));
  const total = maxVal + 1;
  const baseSize = Math.floor(total / count);
  const remainder = total % count;
  const newLevels = [];
  let current = 0;
  for (let i = 0; i < count; i++) {
    const segmentSize = baseSize + (i < remainder ? 1 : 0);
    const min = current;
    const max = current + segmentSize - 1;
    current = max + 1;
    const existing = existingLevels[i];
    newLevels.push({
      title: existing?.title ?? PROFICIENCY_LEVEL_LABELS[i] ?? "",
      min,
      max,
    });
  }
  return newLevels;
};

export const buildDefaultProficiencyLevels = (maxPoint) =>
  splitProficiencyPointsAcrossLevels(maxPoint, 5, []);

export const validateProficiencyLevels = (levels, maxValue) => {
  if (!levels || levels.length === 0) {
    return "Add at least one proficiency level.";
  }
  const maxV = Math.floor(Number(maxValue) || 0);
  if (maxV < 0) {
    return "Invalid max points.";
  }

  const parsed = [];
  for (let i = 0; i < levels.length; i++) {
    const row = levels[i];
    const title = String(row?.title ?? "").trim();
    if (!title) {
      return `Level ${i + 1}: title is required.`;
    }
    const minRaw = row?.min;
    const maxRaw = row?.max;
    if (minRaw === "" || minRaw === null || minRaw === undefined) {
      return `Level ${i + 1}: min is required.`;
    }
    if (maxRaw === "" || maxRaw === null || maxRaw === undefined) {
      return `Level ${i + 1}: max is required.`;
    }
    const min = Number(minRaw);
    const max = Number(maxRaw);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return `Level ${i + 1}: min and max must be valid numbers.`;
    }
    if (!Number.isInteger(min) || !Number.isInteger(max)) {
      return `Level ${i + 1}: min and max must be whole numbers.`;
    }
    if (min > max) {
      return `Level ${i + 1}: min cannot be greater than max.`;
    }
    if (min < 0 || max > maxV) {
      return `Level ${i + 1}: range must stay between 0 and ${maxV}.`;
    }
    parsed.push({ min, max, order: i });
  }

  parsed.sort((a, b) => a.min - b.min || a.order - b.order);

  let expected = 0;
  for (let j = 0; j < parsed.length; j++) {
    const { min, max, order } = parsed[j];
    if (min < expected) {
      return `Proficiency ranges overlap or are out of order (check level ${order + 1}).`;
    }
    if (min > expected) {
      return `Points ${expected} to ${min - 1} are not covered by any proficiency level. Cover every point from 0 to ${maxV} with no gaps.`;
    }
    expected = max + 1;
  }
  if (expected !== maxV + 1) {
    return `Proficiency ranges must cover every point from 0 through ${maxV}. Points ${expected} to ${maxV} are missing.`;
  }
  return null;
};

export const getProficiencyConfigRows = (orgConfig, examCode) => {
  if (!orgConfig || !examCode) return [];
  const nested = orgConfig.config?.proficiency_config?.[examCode];
  if (Array.isArray(nested) && nested.length > 0) return nested;
  const legacy = orgConfig.proficiency_config?.[examCode];
  if (Array.isArray(legacy) && legacy.length > 0) return legacy;
  return [];
};

export const isProficiencyBandConfigActive = (rows, maxPoints) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const maxV = Math.floor(Number(maxPoints) || 0);
  if (maxV < 0) return false;
  return validateProficiencyLevels(rows, maxV) === null;
};

const proficiencyLabelFromPercentage = (numGrade, maxV) => {
  if (!Number.isFinite(maxV) || maxV <= 0) return "-";
  const percentage = (numGrade / maxV) * 100;
  if (percentage <= 20) return PROFICIENCY_LEVEL_LABELS[0];
  if (percentage <= 40) return PROFICIENCY_LEVEL_LABELS[1];
  if (percentage <= 60) return PROFICIENCY_LEVEL_LABELS[2];
  if (percentage <= 80) return PROFICIENCY_LEVEL_LABELS[3];
  return PROFICIENCY_LEVEL_LABELS[4];
};

export const getOverallProficiencyLabelFromPoints = (grade, maxPoints, rows) => {
  const numGrade = Number(grade);
  if (Number.isNaN(numGrade)) return "-";
  const maxV = Number(maxPoints);
  if (!Number.isFinite(maxV) || maxV <= 0) return "-";
  if (isProficiencyBandConfigActive(rows, maxV)) {
    for (let i = 0; i < rows.length; i += 1) {
      const r = rows[i];
      const min = Number(r.min);
      const max = Number(r.max);
      if (Number.isFinite(min) && Number.isFinite(max) && numGrade >= min && numGrade <= max) {
        const t = String(r.title ?? "").trim();
        if (t) return t;
      }
    }
  }
  return proficiencyLabelFromPercentage(numGrade, maxV);
};

export const getZeroScoreProficiencyLevelName = (maxPoints, rows) => {
  const label = getOverallProficiencyLabelFromPoints(0, maxPoints, rows);
  if (label && label !== "-") return label;
  return PROFICIENCY_LEVEL_LABELS[0] || "Novice";
};

export const sumMaxPointsForCategory = (questions, categoryType) => {
  if (!Array.isArray(questions)) return 0;
  return questions.reduce((acc, q) => {
    if (q?.category_type !== categoryType) return acc;
    return acc + Number(q.max_point || 0);
  }, 0);
};

export const sumSubmissionCategoryPoints = (submission, questions, categoryType) => {
  if (!submission || !Array.isArray(questions)) return 0;
  const aiGrade = submission.ai_grade;
  if (!aiGrade) return 0;
  let total = 0;
  questions.forEach((section, sectionIndex) => {
    if (section.category_type !== categoryType) return;
    if (section?.rubric_id) {
      const aiDt = submission?.ai_grading_by_sections?.[sectionIndex];
      if (aiDt) {
        total += Number(aiDt?.grade_point) || 0;
      }
    } else {
      const submissionAnswer = aiGrade[sectionIndex];
      if (submissionAnswer && Array.isArray(submissionAnswer)) {
        total += submissionAnswer.reduce(
          (acc, curr) => acc + (Number(curr?.ai_grade_point) || 0),
          0,
        );
      }
    }
  });
  return total;
};

export const isPlacementZeroDomainCriteriaEnabled = (orgConfig) =>
  Boolean(orgConfig?.config?.criteria && orgConfig.config.criteria.enable);

export const getOverallProficiencyLabelForSubmission = (submission, maxPoints, rows, options) => {
  if (!submission || !submission.graded) return "-";
  const orgConfig = options?.orgConfig;
  const assignment = options?.assignment;
  if (
    isPlacementZeroDomainCriteriaEnabled(orgConfig) &&
    assignment?.questions?.length
  ) {
    const speakingMax = sumMaxPointsForCategory(assignment.questions, "speaking");
    const readingMax = sumMaxPointsForCategory(assignment.questions, "reading");
    const speakingScore = sumSubmissionCategoryPoints(submission, assignment.questions, "speaking");
    const readingScore = sumSubmissionCategoryPoints(submission, assignment.questions, "reading");
    if (
      (speakingMax > 0 && speakingScore === 0) ||
      (readingMax > 0 && readingScore === 0)
    ) {
      return getOverallProficiencyLabelFromPoints(0, maxPoints, rows);
    }
  }
  return getOverallProficiencyLabelFromPoints(submission.grade, maxPoints, rows);
};

const defaultRow = () => ({
  title: "",
  min: "",
  max: "",
});

const ProficiencyConfiguration = ({
  levels = [],
  onChange,
  disabled = false,
  max_value,
  criteriaEnable = false,
  onCriteriaEnableChange,
}) => {
  useEffect(() => {
    if (!disabled && levels.length === 0 && (max_value || max_value === 0)) {
      onChange(buildDefaultProficiencyLevels(max_value));
    }
  }, [levels, disabled, onChange, max_value]);

  const handleChange = (index, field, value) => {
    const updated = [...levels];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleAddLevel = (index = null) => {
    const updated = [...levels];
    if (index !== null) {
      updated.splice(index + 1, 0, defaultRow());
    } else {
      updated.push(defaultRow());
    }
    onChange(updated);
  };

  const handleDeleteLevel = (index) => {
    const updated = [...levels];
    updated.splice(index, 1);
    onChange(updated);
  };

  const handleCalculateRange = () => {
    const levelCount = Math.max(1, levels.length);
    const next = splitProficiencyPointsAcrossLevels(max_value, levelCount, levels);
    onChange(next);
  };

  const zeroScoreLevelName = useMemo(
    () => getZeroScoreProficiencyLevelName(max_value, levels),
    [max_value, levels],
  );

  return (
    <div style={{ width: "100%", marginTop: 24 }}>
      <div className="flex items-center justify-between mb-10px">
        <Typography variant="h6" gutterBottom>
          Proficiency Configuration
        </Typography>
        <div className="flex items-center gap-10px">
          <div style={{ whiteSpace: "nowrap" }}>
            Max Points: <b>{max_value}</b>
          </div>
          <BadgeButton
            style={{ width: "fit-content" }}
            variant="contained"
            color="primary"
            startIcon={<CalculatorOutlined />}
            onClick={handleCalculateRange}
            disabled={disabled || (!max_value && max_value !== 0)}
          >
            Calculate Range
          </BadgeButton>
        </div>
      </div>
      {levels.map((level, index) => (
        <Card
          key={index}
          style={{ marginBottom: 16 }}
          title={`Level ${index + 1}`}
          extra={
            !disabled && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteLevel(index)}
              />
            )
          }
        >
          <Row gutter={12}>
            <Col span={12}>
              <Input
                placeholder="Level Title"
                value={level.title}
                onChange={(e) => handleChange(index, "title", e.target.value)}
                disabled={disabled}
              />
            </Col>
            <Col span={6}>
              <Input
                type="number"
                placeholder="Min"
                value={level.min}
                onChange={(e) => handleChange(index, "min", e.target.value)}
                disabled={disabled}
              />
            </Col>
            <Col span={6}>
              <Input
                type="number"
                placeholder="Max"
                value={level.max}
                onChange={(e) => handleChange(index, "max", e.target.value)}
                disabled={disabled}
              />
            </Col>
          </Row>
          {!disabled && (
            <Row justify="end" style={{ marginTop: 10 }}>
              <Button type="dashed" onClick={() => handleAddLevel(index)}>
                Add Below
              </Button>
            </Row>
          )}
        </Card>
      ))}
      {!disabled && (
        <Row justify="start" style={{ marginTop: 8 }}>
          <MUIButton
            variant="contained"
            color="primary"
            startIcon={<PlusOutlined />}
            onClick={() => handleAddLevel(null)}
          >
            Add Proficiency
          </MUIButton>
        </Row>
      )}
      {!disabled && typeof onCriteriaEnableChange === "function" && (
        <Row gutter={[16, 8]} style={{ marginTop: 20 }}>
          <Col span={24}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <Switch checked={Boolean(criteriaEnable)} onChange={onCriteriaEnableChange} />
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Enable placement criteria (zero in speaking or reading)
              </Typography>
            </div>
            <Typography variant="body2" style={{ color: "#d32f2f", marginTop: 8, maxWidth: 720 }}>
              The student received a score of &quot;0&quot; in one of the domains; therefore, we placed the
              student at the {zeroScoreLevelName} proficiency level.
            </Typography>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ProficiencyConfiguration;
